import { deckFaceUrl, deckFromResponse, parseDeck, pickFaceUrl } from '@lunary/app-core/deck-model'
import type { DeckView, FaceCard } from '@lunary/app-core/deck-model'
import { useSyncExternalStore } from 'react'
import { cardFace } from './card-faces'

export type { DeckView, FaceCard }

const STORAGE_KEY = 'lunary.deck.v1'
const listeners = new Set<() => void>()
let current: DeckView | null = readCache()

function readCache(): DeckView | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? parseDeck(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

function writeCache(deck: DeckView | null): void {
  try {
    if (deck) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(deck))
    else window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    return
  }
}

function fromResponse(body: unknown, base: URL): DeckView | null {
  return deckFromResponse(body, (path) => new URL(path, base).href)
}

function publish(next: DeckView | null): void {
  if (JSON.stringify(next) === JSON.stringify(current)) return
  current = next
  writeCache(next)
  listeners.forEach((l) => l())
}

type DeckSource = { readonly base: URL; readonly token: () => string | null }

let source: DeckSource | null = null
let generation = 0

function load(): void {
  if (!source) return
  const { base, token } = source
  const run = ++generation
  const bearer = token()
  const headers: Record<string, string> = { accept: 'application/json' }
  if (bearer) headers.authorization = `Bearer ${bearer}`
  fetch(new URL('decks', base), { headers, cache: 'no-cache' })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
    .then((body: unknown) => {
      if (run === generation) publish(fromResponse(body, base))
    })
    .catch(() => undefined)
}

export function initDeck(apiBase: string, token: () => string | null = () => null): void {
  source = { base: new URL(apiBase.endsWith('/') ? apiBase : `${apiBase}/`, window.location.href), token }
  load()
}

export function reloadDeck(): void {
  load()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useDeck(): DeckView | null {
  return useSyncExternalStore(subscribe, () => current, () => null)
}

export function cardFaceFor(deck: DeckView | null, number: number): string | null {
  return deckFaceUrl(deck, number) ?? cardFace(number)
}

export function resolveApiUrl(path: string | null | undefined): string | null {
  if (!path) return null
  try {
    return new URL(path, source?.base ?? window.location.href).href
  } catch {
    return null
  }
}

export function pickFaceFor(deck: DeckView | null, card: FaceCard): string | null {
  return pickFaceUrl(deck, card.slug) ?? (card.arcana === 'major' ? cardFace(card.number) : null)
}

export function usePickFace(card: FaceCard | null): string | null {
  const deck = useDeck()
  return card ? pickFaceFor(deck, card) : null
}

export function useCardFace(number: number): string | null {
  return cardFaceFor(useDeck(), number)
}

export type CardBack = { readonly kind: 'image'; readonly src: string } | { readonly kind: 'builtin' }

const BUILTIN_BACK: CardBack = { kind: 'builtin' }
const backSeed = Math.random()

export function cardBackFor(deck: DeckView | null): CardBack {
  const pool = deck?.backs.length ? deck.backs : deck?.back ? [deck.back] : []
  const src = pool[Math.floor(backSeed * pool.length)]
  return src ? { kind: 'image', src } : BUILTIN_BACK
}

export function useCardBack(): CardBack {
  return cardBackFor(useDeck())
}
